import React, { useCallback, useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import profilePics from "../Assets/applicant-pics.svg";
import "react-calendar/dist/Calendar.css";
import { useUserContext } from "../../Contexts/UserContext";
// import { useCaseContext } from "../../Contexts/CaseContext";

dayjs.extend(isSameOrAfter);

interface CourtCases {
	basicData: BasicData;
	caseType: CaseType;
	claimants: ClaimantDetails[];
	defendants: DefendantDetails[];
	nextVirtualSchedule: {
		id: string;
		title: string;
		status: string;
		courtId: string;
		suitNumber: string;
		createdAt: string;
		updatedAt: string | null;
		sessionDate: string;
		caseId: string;
		lastReminderSent: string | null;
	};

	judge: {
		id: string;
		fullName: string;
		firstName: string;
		middleName: string;
		lastName: string;
		username: string;
		scnNumber: string;
		email: string;
	};
}

interface BasicData {
	id: string;
	title: string;
	nextSittingDate: Date;
	createdOn: string;
	suitNumber?: string;
	paidStatus: boolean;
}
interface CaseType {
	description: string;
	id: string;
	title: string;
}
interface ClaimantDetails {
	firstName: string;
	middleName: string;
	lastName: string;
	mobile: string;
	imageUrl: string;
}
interface DefendantDetails {
	firstName: string;
	middleName: string;
	lastName: string;
	mobile: string;
	imageUrl: string;
}
const CourtSchedule: React.FC = () => {
	const [value, setValue] = useState<Date | null>(new Date());
	const [courtCases, setCourtCases] = useState<CourtCases[]>([]);
	const [loading, setLoading] = useState(false);
	// const navigate = useNavigate();
	const { userData } = useUserContext();
	// const { caseData } = useCaseContext();

	const dateValue = dayjs(value?.toString()).format("MM-DD-YYYY");

	const getCourtSchedule = useCallback(
		async () => {
		const authToken = sessionStorage.getItem("token");

		if (!authToken) {
			console.error("No auth token found");
			return;
		}

		let allCases: CourtCases[] = [];
		let currentPage = 1;
		let hasMorePages = true;

		try {
			setLoading(true);

			while (hasMorePages) {
				const url = `https://backend-kyw8.onrender.com/my-cases?page=${currentPage}&fromDate=${dateValue}`;

				const response = await fetch(url, {
					headers: {
						Authorization: `Bearer ${authToken}`,
					},
				});

				if (!response.ok) {
					if (response.status === 401) {
						throw new Error("Unauthorized. Please login again.");
					}
					throw new Error("Something went wrong");
				}

				const newData = await response.json();

				// Add the current page's cases to the allCases array
				allCases = [...allCases, ...newData.cases];

				// Check if there are more pages
				if (newData.hasMorePages) {
					currentPage += 1;
				} else {
					hasMorePages = false;
				}
			}
			setCourtCases(allCases);
			console.log(allCases);
		} catch (error: any) {
			console.error(error.message);
		} finally {
			setLoading(false);
		}
	}, [dateValue])

	useEffect(() => {
		if (value) {
			getCourtSchedule();
		}
	}, [getCourtSchedule, value]);

	const upcomingCases = courtCases
		?.filter((cases) =>
			dayjs(cases?.basicData?.nextSittingDate).isSameOrAfter(dayjs())
		)
		.sort((a, b) =>
			dayjs(a?.basicData?.nextSittingDate).isAfter(
				dayjs(b?.basicData?.nextSittingDate)
			)
				? 1
				: -1
		);

	const nextCase = upcomingCases?.[0];

	function isSameDate(givenDate: Date | string | null): boolean {
		if (!givenDate) {
			return false;
		}

		const dateToCompare = new Date(givenDate); // Convert string to Date if necessary

		// Ensure the givenDate is valid before proceeding
		if (isNaN(dateToCompare.getTime())) {
			console.error("Invalid date");
			return false;
		}

		const today = new Date(); // Get today's date

		return (
			today.getFullYear() === dateToCompare.getFullYear() &&
			today.getMonth() === dateToCompare.getMonth() &&
			today.getDate() === dateToCompare.getDate()
		);
	}

	return (
		<div className="flex flex-col gap-8">
			<p className="text-black text-2xl font-bold">
				{userData.userRole === "COUNSEL" ? "Court Schedule" : "Cause List"}
			</p>
			<div className="flex md:flex-row flex-col gap-[46px]">
				<div className="h-auto flex flex-col gap-10 md:w-1/2 w-full">
					<div className="w-full h-1/1 calendar">
						<Calendar
							onChange={(value) => setValue(value as Date)}
							value={value}
						/>
					</div>

					<div className="h-auto">
						<p className="text-black text-xl font-bold">Upcoming Cases</p>

						<div className="flex flex-col gap-5 mt-9">
							{loading === true ? (
								<div>Loading...</div>
							) : (
								<>
									{upcomingCases?.map((cases) => {
										const caseDate = new Date(
											cases?.basicData?.nextSittingDate
										);

										return (
											<div
												className="flex md:flex-row flex-col md:justify-between md:gap-0 gap-3"
												key={cases?.basicData?.id}>
												<div className="flex md:gap-8 gap-3 md:flex-row flex-col">
													<p className="text-black text-sm font-normal">
														{new Intl.DateTimeFormat("en-US", {
															month: "long",
															day: "numeric",
															year: "numeric",
														}).format(caseDate)}
													</p>
													<p className="text-black text-sm font-semibold text-wrap md:text-center text-start">
														{cases?.basicData?.title}
													</p>
												</div>
												<p className="text-black text-sm font-normal">
													{new Intl.DateTimeFormat("en-US", {
														timeZone: "UTC",
														hour: "numeric",
														minute: "numeric",
														hour12: true,
													}).format(caseDate)}
												</p>
											</div>
										);
									})}
								</>
							)}
						</div>
					</div>
				</div>

				<div className="md:w-1/2 w-full flex flex-col gap-[50px]">
					<div className="h-auto flex flex-col gap-3 md:mt-0 mt-9">
						<p className="text-black text-lg font-bold">{`Court cases for ${dateValue}`}</p>

						<div className="flex flex-col gap-5">
							{loading === true ? (
								<div>Loading...</div>
							) : (
								<>
									{courtCases
										?.filter((cases) => {
											const caseDate = new Date(
												cases?.basicData?.nextSittingDate
											);
											const selectedDate = new Date(dateValue);

											return (
												caseDate.getFullYear() === selectedDate.getFullYear() &&
												caseDate.getMonth() === selectedDate.getMonth() &&
												caseDate.getDate() === selectedDate.getDate()
											);
										})
										.map((cases) => {
											const caseDate = new Date(
												cases?.basicData?.nextSittingDate
											);

											return (
												<div
													className="flex md:flex-row flex-col md:justify-between md:gap-0 gap-3"
													key={cases?.basicData?.id}>
													<div className="flex md:gap-8 gap-3 md:flex-row flex-col">
														<p className="text-black text-sm font-normal">
															{new Intl.DateTimeFormat("en-US", {
																month: "long",
																day: "numeric",
																year: "numeric",
															}).format(caseDate)}
														</p>
														<p className="text-black text-sm font-semibold text-wrap md:text-center text-start">
															{cases?.basicData?.title}
														</p>
													</div>
													<p className="text-black text-sm font-normal">
														{new Intl.DateTimeFormat("en-US", {
															timeZone: "UTC",
															hour: "numeric",
															minute: "numeric",
															hour12: true,
														}).format(caseDate)}{" "}
														{"(UTC)"}
													</p>
												</div>
											);
										})}
								</>
							)}
						</div>
					</div>

					<div className="md:w-1/2 w-full flex flex-col gap-[50px]">
						<div className="h-auto flex flex-col gap-3 md:mt-0 mt-9">
							<p className="text-black text-lg font-bold">Next Case Details</p>
							{nextCase ? (
								<>
									<div className="flex gap-[100px]">
										<p className="text-black text-sm font-medium w-fit">
											Case Type:{" "}
											<span className="font-normal">
												{nextCase.caseType.title}
											</span>
										</p>
										<p className="text-black text-sm font-medium">
											SUIT NUMBER:{" "}
											<span className="font-normal">
												{nextCase.basicData.suitNumber}
											</span>
										</p>
									</div>

									<p className="text-black text-sm font-medium">
										Date & Time {"(UTC)"}:{" "}
										<span className="font-normal">
											{nextCase?.basicData?.nextSittingDate
												? new Intl.DateTimeFormat("en-US", {
														timeZone: "UTC",
														month: "long",
														day: "numeric",
														year: "numeric",
														hour: "numeric",
														minute: "numeric",
														hour12: true,
												  }).format(
														new Date(nextCase?.basicData.nextSittingDate)
												  )
												: "Date not available"}
										</span>
									</p>
									{!nextCase.basicData.paidStatus && (
										<p className="text-black text-sm font-medium">
											Payment Status:{" "}
											<span className="font-normal">Unpaid </span>
										</p>
									)}
									{nextCase.basicData.paidStatus &&
										isSameDate(nextCase.basicData.nextSittingDate) &&
										userData.userRole === "JUDGE" && (
											<p className="text-black text-sm font-medium">
												Start Court Session:{" "}
												<span className="">
													<a
														href={`https://scheduling-app-three.vercel.app/ext-court-live-session/${nextCase.nextVirtualSchedule.id}/${nextCase.nextVirtualSchedule.title}/${userData.email}/JUDGE`}
														className="text-blue-500 underline"
														target="_blank" rel="noreferrer">
														Here
													</a>
												</span>
											</p>
										)}
									{nextCase.basicData.paidStatus &&
										isSameDate(nextCase.basicData.nextSittingDate) &&
										userData.userRole !== "JUDGE" && (
											<p className="text-black text-sm font-medium">
												Join Court Session:{" "}
												<span className="">
													<a
														href={`https://scheduling-app-three.vercel.app/ext-court-live-session/${nextCase.nextVirtualSchedule.id}/${nextCase.nextVirtualSchedule.title}/${userData.email}/${userData.userRole}`}
														className="text-blue-500 underline"
														target="_blank" rel="noreferrer">
														Here
													</a>
												</span>
											</p>
										)}

									<div className="flex flex-col gap-[21px]">
										<p className="text-black text-lg font-bold">
											Claimant Details
										</p>
										<div className="flex gap-[11px]">
											<img src={profilePics} alt="profile-pics" />
											<div className="flex flex-col gap-[26px]">
												<p className="text-sm font-medium">
													Name:{" "}
													<span className="text-sm font-normal">{`${
														nextCase?.claimants[0]?.firstName
													} ${nextCase?.claimants[0]?.middleName || ""} ${
														nextCase?.claimants[0]?.lastName
													}`}</span>
												</p>
												<p className="text-sm font-medium">
													Phone Number:{" "}
													<span className="text-sm font-normal">
														{nextCase?.claimants[0]?.mobile}
													</span>
												</p>
											</div>
										</div>
									</div>

									<div className="flex flex-col gap-[21px]">
										<p className="text-black text-lg font-bold">
											Defendant Details
										</p>
										<div className="flex gap-[11px]">
											<img src={profilePics} alt="profile-pics" />
											<div className="flex flex-col gap-[26px]">
												<p className="text-sm font-medium">
													Name:{" "}
													<span className="text-sm font-normal">{`${
														nextCase?.defendants[0]?.firstName
													} ${nextCase?.defendants[0]?.middleName || ""} ${
														nextCase?.defendants[0]?.lastName
													}`}</span>
												</p>
												<p className="text-sm font-medium">
													Phone Number:{" "}
													<span className="text-sm font-normal">
														{nextCase?.defendants[0]?.mobile}
													</span>
												</p>
											</div>
										</div>
									</div>
								</>
							) : (
								<p>No upcoming cases available.</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

type ValuePiece = Date | null;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type Value = ValuePiece | [ValuePiece, ValuePiece];

export default CourtSchedule;
